import { Container } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import adminApi from "../../../api/adminApi";
import type { FieldIdToValueMap } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import useGetObjectPageLayout from "../../../hooks/useGetObjectPageLayout";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import FullHeightFlexPage from "../../common/layout/FullHeightFlexPage";
import StretchedScrollableContent from "../../common/layout/StretchedScrollableContent";
import ObjectTitle from "../../common/ObjectTitle";
import FieldValuesSection from "../FieldValuesSection";

const createObject = withErrorHandling(adminApi.createObject);

interface Props {
  objectType?: string;
  entityTypeCode?: string;
  backButtonPath?: string;
}

const NewObjectDetailsPage = ({ objectType, entityTypeCode, backButtonPath }: Props) => {
  usePageTitle("New Record");

  const { type } = useParams();
  const { hasAnyPermission } = useClientContext();
  const navigate = useNavigate();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { pathname } = useLocation();
  const resolvedBackButtonPath = backButtonPath ?? "..";

  const resolvedObjectType = useMemo(() => objectType || type || "", [objectType, type]);

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition(resolvedObjectType), [resolvedObjectType]);

  const [objectDefinitionWithPermissions, fetchDefinitionError] = useFetch(getObjectDefinition);

  const getCardLayout = useGetObjectPageLayout(resolvedObjectType);

  const [cardLayout, fetchCardLayoutError] = useFetch(getCardLayout);

  if (fetchDefinitionError || fetchCardLayoutError) {
    logError(fetchDefinitionError || fetchCardLayoutError, "[NewProductObjectDetailsPage]");
    return <DataLoadingFailed />;
  }

  if (!objectDefinitionWithPermissions || !cardLayout) {
    return <InlineLoader />;
  }

  if (!resolvedObjectType) {
    return <Navigate to={resolvedBackButtonPath} />;
  }

  const { objectClassDefinition, objectPermissions } = objectDefinitionWithPermissions;

  const hasPermissionsToManageObjectData = hasAnyPermission(objectPermissions.objectDataWritePermissions);

  const handleUpdate = async (fieldValues: FieldIdToValueMap) => {
    const [object, error] = await createObject(resolvedObjectType, {
      fieldValuesUserInput: fieldValues,
      entityTypeCode,
    });
    if (error) {
      logError(error, "[NewProductObjectDetailsPage]");
      sendNotificationError(error.message);
      return false;
    }

    sendNotification("Object created successfully");
    await wait(500);

    const objectDetailsPath = pathname.replace(/(.*)\/new$/, `$1/${object.id}`);
    navigate(objectDetailsPath);

    return true;
  };

  return (
    <FullHeightFlexPage>
      <GeneralPageHeader
        TitleComponent={<ObjectTitle title="New Record" typeName={objectClassDefinition.objectName} />}
        showDefaultBackButtonTo={resolvedBackButtonPath}
      />
      <StretchedScrollableContent>
        <Container maxWidth="lg" disableGutters sx={{ height: "100%", px: 3, pt: 2.5 }}>
          <FieldValuesSection
            objectType={objectClassDefinition.objectType}
            objectId=""
            businessCentralType={objectClassDefinition.businessCentralType}
            fields={objectClassDefinition.fields.filter((f) => !f.attributes.includes("ReadOnly"))}
            cardLayout={cardLayout}
            fieldValues={{}}
            saveFieldValues={handleUpdate}
            allowEditing={hasPermissionsToManageObjectData}
          />
        </Container>
      </StretchedScrollableContent>
    </FullHeightFlexPage>
  );
};

export default NewObjectDetailsPage;
